import { ChangeEvent, FormEvent, useState } from "react";
import { Navigation } from "@/components/ui/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";

const issueTypes = [
	{ value: "bug", label: "Bug or glitch" },
	{ value: "content", label: "Incorrect or missing content" },
	{ value: "performance", label: "Performance issue" },
	{ value: "billing", label: "Billing or account" },
	{ value: "other", label: "Something else" },
];

export default function ReportIssue() {
	const { toast } = useToast();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files ? Array.from(event.target.files) : [];
		setSelectedFiles(files);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmitted(false);
		const form = event.currentTarget;
		const formData = new FormData(form);

		const name = (formData.get("name")?.toString() ?? "").trim();
		const email = (formData.get("email")?.toString() ?? "").trim();
		const subject = (formData.get("subject")?.toString() ?? "").trim();
		const typeValue = (formData.get("type")?.toString() ?? "").trim();
		const typeLabel = issueTypes.find((issue) => issue.value === typeValue)?.label ?? (typeValue || "n/a");
		const details = formData.get("details")?.toString() ?? "";
		const urgency = (formData.get("urgency")?.toString() ?? "").trim();

		const payload = {
			name,
			email,
			subject,
			type: typeLabel,
			details,
			urgency,
			attachments: selectedFiles.map((file) => ({
				name: file.name,
				size: file.size,
				type: file.type,
			})),
		};

		console.info("[ReportIssue] New submission", payload);
		setIsSubmitting(true);

		const attachmentsSummary = selectedFiles
			.map((file) => `- ${file.name} (${Math.round(file.size / 1024)} KB)`)
			.join("\n");

		const bodySections = [
			`Name: ${name || "n/a"}`,
			`Email: ${email || "n/a"}`,
			`Issue type: ${typeLabel}`,
			`Urgency: ${urgency || "n/a"}`,
			"",
			"Details:",
			details || "n/a",
		];

		if (attachmentsSummary) {
			bodySections.push(
				"",
				"Attachments (please reattach these files to your email):",
				attachmentsSummary
			);
		}

		const body = bodySections.join("\n");
		const mailtoUrl = `mailto:serikanj.lucen@gmail.com?subject=${encodeURIComponent(subject || "New issue report")}&body=${encodeURIComponent(body)}`;

		window.location.href = mailtoUrl;

		setIsSubmitting(false);
		setSubmitted(true);
		toast({
			title: "Thanks for letting us know!",
			description: "Your issue report has been captured. We’ll follow up if we need more details.",
		});
		form.reset();
		setSelectedFiles([]);
	};

	return (
		<div className="min-h-screen bg-background">
			<Navigation />
			<main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="max-w-5xl mx-auto grid items-start gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
					<Card className="border border-neutral-200 shadow-sm">
						<CardHeader>
							<CardTitle>Report an Issue</CardTitle>
							<CardDescription>
								Spotted a problem? Share a few details so we can investigate quickly.
							</CardDescription>
						</CardHeader>
						<CardContent>
							{submitted && (
								<Alert className="mb-6">
									<AlertTitle>Issue reported</AlertTitle>
									<AlertDescription>
										We appreciate the heads-up. Our team reads every submission.
									</AlertDescription>
								</Alert>
							)}
							<form className="space-y-6" onSubmit={handleSubmit}>
								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="name">Your name</Label>
										<Input
											id="name"
											name="name"
											placeholder="Jane Doe"
											autoComplete="name"
											required
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="email">Email</Label>
										<Input
											id="email"
											type="email"
											name="email"
											placeholder="you@email.com"
											autoComplete="email"
											required
										/>
									</div>
								</div>

								<div className="space-y-2">
									<Label htmlFor="subject">Subject</Label>
									<Input
										id="subject"
										name="subject"
										placeholder="Brief summary of the issue"
										required
									/>
								</div>

								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="type">Issue type</Label>
										<select
											id="type"
											name="type"
											required
											defaultValue=""
											className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/80 focus-visible:ring-offset-2"
										>
											<option value="" disabled>
												Select an option
											</option>
											{issueTypes.map((issue) => (
												<option key={issue.value} value={issue.value}>
													{issue.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="urgency">Urgency</Label>
										<select
											id="urgency"
											name="urgency"
											defaultValue="normal"
											className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/80 focus-visible:ring-offset-2"
										>
											<option value="low">Low</option>
											<option value="normal">Normal</option>
											<option value="high">High</option>
										</select>
									</div>
								</div>

								<div className="space-y-2">
									<Label htmlFor="details">What happened?</Label>
									<Textarea
										id="details"
										name="details"
										placeholder="Share any steps to reproduce, screenshots, or context that helps us understand the issue."
										rows={6}
										required
									/>
									<div className="space-y-2">
										<Label htmlFor="attachments">Attach images (optional)</Label>
										<input
											id="attachments"
											name="attachments"
											type="file"
											accept="image/*"
											multiple
											onChange={handleFileChange}
											className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
										/>
										{selectedFiles.length > 0 && (
											<ul className="space-y-1 text-xs text-muted-foreground">
												{selectedFiles.map((file) => (
													<li key={file.name}>
														{file.name} ({Math.round(file.size / 1024)} KB)
													</li>
												))}
											</ul>
										)}
									</div>
								</div>

								<Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
									{isSubmitting ? "Submitting..." : "Submit issue"}
								</Button>
							</form>
						</CardContent>
					</Card>
					<Card className="border border-neutral-200 shadow-sm">
						<CardHeader>
							<CardTitle>Need a faster response?</CardTitle>
							<CardDescription>Here are a few other ways to reach the team.</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4 text-sm text-muted-foreground">
							<div>
								<p className="font-medium text-foreground">Email support</p>
								<p>
									Send us a message at{" "}
									<a className="text-primary underline" href="mailto:serikanj.lucen@gmail.com">
										serikanj.lucen@gmail.com
									</a>
									
								</p>
							</div>
						</CardContent>
					</Card>
				</div>
			</main>
		</div>
	);
}